import { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import {
  LuCircleHelp,
  LuLoaderCircle,
  LuTriangleAlert,
  LuUndo2,
} from "react-icons/lu";
import { components } from "../../design/tokens";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { useProjectsListActions } from "../../core_stores/useProjectsListStore";
import { useProjectStore } from "../../core_stores/useProjectStore";
import { HttpBadRequestError } from "../../model/httpException";
import { useProjectModal } from "../../providers/ProjectModalProvider";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { useProjectConfigActions } from "../../view_stores/useProjectConfigStore";
import {
  useProjectSettingsActions,
  useProjectSettingsAppName,
  useProjectSettingsAppNameError,
  useProjectSettingsAppVersion,
  useProjectSettingsAppVersionError,
  useProjectSettingsCanSubmit,
  useProjectSettingsPackageName,
  useProjectSettingsPackageNameError,
} from "../../view_stores/useProjectSettingsStore";
import ProjectSettingsPackageNameConfirmation from "./components/ProjectSettingsPackageNameConfirmation";

export default function ProjectSettings() {
  const appName = useProjectSettingsAppName();
  const isCompactView = useIsCompactView();

  const {
    initialize: initializeProjectSettings,
    dispose: disposeProjectSettings,
  } = useProjectSettingsActions();

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", title: "Workspace Settings" });
    initializeProjectSettings();

    return () => {
      disposeProjectSettings();
    };
  }, [initializeProjectSettings, disposeProjectSettings]);

  return (
    <>
      <div
        className={`${
          isCompactView ? "py-4" : "py-8"
        } flex flex-col gap-6 px-8 max-w-[700px] mx-auto h-full`}
      >
        {!appName ? (
          <div className="h-full flex items-center justify-center">
            <LuLoaderCircle className="animate-spin" size={24} />
          </div>
        ) : (
          <>
            <AppName />
            <PackageName />
            <AppVersion />
            <Buttons />
          </>
        )}
      </div>
    </>
  );
}

function AppName() {
  const appName = useProjectSettingsAppName();
  const appNameError = useProjectSettingsAppNameError();
  const { appNameChanged } = useProjectSettingsActions();

  const canUndo = appName?.current !== appName?.original;

  return (
    <EditableValue
      label="App name"
      value={appName?.current ?? ""}
      error={appNameError}
      tooltipMsg="This is the name shown under your app icon. To avoid it getting cut off on devices, it is recommended to keep it within 11–13 characters."
      onUndo={() => appNameChanged(appName?.original ?? "")}
      onChange={(value) => appNameChanged(value)}
      canUndo={canUndo}
    />
  );
}

function PackageName() {
  const packageName = useProjectSettingsPackageName();
  const packageNameError = useProjectSettingsPackageNameError();
  const { packageNameChanged } = useProjectSettingsActions();

  const canUndo = packageName?.current !== packageName?.original;

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="px-1">
        <label className={components.form.label}>
          <span>Package name</span>
          {canUndo && (
            <span
              data-tooltip-id="dashboard-tooltip"
              data-tooltip-content="If your mobile application has been already published, changing its package name will prevent you from updating your application."
            >
              <LuTriangleAlert
                size={components.form.helpIconSize}
                className="text-[#D8C640] cursor-pointer"
              />
            </span>
          )}
          <span
            data-tooltip-id="dashboard-tooltip"
            data-tooltip-content="Package name must start with a letter, include only letters, numbers, underscores, and dots, and have at least two parts separated by a dot."
          >
            <LuCircleHelp size={components.form.helpIconSize} className="cursor-pointer" />
          </span>
        </label>
      </div>
      <div className="relative">
        <input
          type="text"
          className={components.form.inputWithAction(canUndo)}
          value={packageName?.current ?? ""}
          placeholder="Unknown"
          onChange={(e) => packageNameChanged(e.target.value)}
        />
        <LuUndo2
          size={20}
          className={`${
            canUndo ? "opacity-100" : "opacity-0"
          } absolute right-3 top-1/2 -translate-y-1/2
            text-dark-placeholder
            hover:opacity-50 hover:cursor-pointer
            transition-all duration-300`}
          onClick={() => packageNameChanged(packageName?.original ?? "")}
        />
      </div>
      {packageNameError && (
        <p className={components.form.error}>{packageNameError}</p>
      )}
    </div>
  );
}

function AppVersion() {
  const appVersion = useProjectSettingsAppVersion();
  const appVersionError = useProjectSettingsAppVersionError();
  const { appVersionChanged } = useProjectSettingsActions();

  const canUndo = appVersion?.current !== appVersion?.original;

  return (
    <EditableValue
      label="App version"
      value={appVersion?.current ?? ""}
      error={appVersionError}
      canUndo={canUndo}
      tooltipMsg="App version should use three numbers separated by dots (e.g., 1.0.0) and ideally follow the Semantic Versioning guidelines described at <a href='https://semver.org' target='_blank' rel='noopener noreferrer' style='color: #3B82F6; text-decoration: underline;'>semver.org</a>"
      onChange={(value) => appVersionChanged(value)}
      onUndo={() => appVersionChanged(appVersion?.original ?? "")}
    />
  );
}

function Buttons() {
  const appName = useProjectSettingsAppName();
  const packageName = useProjectSettingsPackageName();
  const canSubmit = useProjectSettingsCanSubmit();

  const { submit, undo } = useProjectSettingsActions();
  const { show, close } = useProjectModal();
  const { loadData: loadProjectConfig } = useProjectConfigActions();
  const { appNameChanged } = useProjectsListActions();
  const { showSnackbar } = useSnackbar();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const hasAppNameBeenChanged = appName?.current !== appName?.original;
    try {
      await submit();
      await loadProjectConfig();
      if (hasAppNameBeenChanged) {
        appNameChanged(
          useProjectStore.getState().projectId!,
          appName?.current ?? ""
        );
      }
      showSnackbar("App settings successfully updated");
    } catch (error) {
      if (error instanceof HttpBadRequestError) {
        showSnackbar(error.body, {
          type: "error",
        });
      }
    }
    setIsSubmitting(false);
  };

  const onClick = () => {
    if (packageName?.current !== packageName?.original) {
      show({
        children: (
          <ProjectSettingsPackageNameConfirmation
            onCancel={close}
            onSave={() => {
              close();
              handleSubmit();
            }}
          />
        ),
        className: "max-w-[550px]",
      });
    } else {
      handleSubmit();
    }
  };

  return (
    <div className={components.form.actions}>
      <button
        className={components.form.cancelButton}
        onClick={undo}
      >
        Cancel
      </button>
      <button
        id="project-settings-save-changes"
        className={components.button.primary}
        disabled={!canSubmit || isSubmitting}
        onClick={onClick}
      >
        {isSubmitting ? (
          <LuLoaderCircle className="animate-spin" size={16} />
        ) : (
          "Save changes"
        )}
      </button>
    </div>
  );
}

function EditableValue({
  label,
  value,
  error,
  canUndo,
  tooltipMsg,
  onChange,
  onUndo,
}: {
  label: string;
  value: string | null;
  error: string | null;
  canUndo: boolean;
  tooltipMsg?: string;
  onChange: (value: string) => void;
  onUndo: () => void;
}) {
  return (
    <div className="w-full flex flex-col gap-3">
      <label className={`${components.form.label} px-1`}>
        <span>{label}</span>
        {tooltipMsg && (
          <span
            data-tooltip-id="dashboard-tooltip"
            data-tooltip-html={tooltipMsg}
          >
            <LuCircleHelp size={components.form.helpIconSize} className="cursor-pointer" />
          </span>
        )}
      </label>
      <div className="relative">
        <input
          type="text"
          className={components.form.inputWithAction(canUndo)}
          value={value ?? ""}
          placeholder="Unknown"
          onChange={(e) => onChange(e.target.value)}
        />
        <LuUndo2
          size={20}
          className={`${
            canUndo ? "opacity-100" : "opacity-0"
          } absolute right-3 top-1/2 -translate-y-1/2
          text-dark-placeholder
          hover:opacity-50 hover:cursor-pointer
          transition-all duration-300`}
          onClick={onUndo}
        />
      </div>
      {error && <p className={components.form.error}>{error}</p>}
    </div>
  );
}
